import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { SessionManager, showAccessDeniedAlert, showTokenExpiredAlert, getConfig } from "../../../utils";

const parseJsonBlob = async (response) => {
    const contentType = response?.headers?.['content-type'] || ''
    if (response?.data instanceof Blob && contentType.includes('json')) {
        try {
            response.data = JSON.parse(await response.data.text())
        } catch (error) {
            return response
        }
    }
    return response
}

export const studentExamCertificate = createAsyncThunk('studentExamCertificate', async ({ examId, studentId, view }) => {
    try {
        const params = { exam_id: examId }
        if (studentId != null && studentId !== '') params.student_id = studentId
        if (view) params.view = true
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + 'student_exam_certificate',
            params,
            responseType: 'blob',
            headers: { Authorization: SessionManager.getCookie('user_token') }
        })
        return parseJsonBlob(res)
    } catch (error) {
        return parseJsonBlob(error.response)
    }
})

const fileNameFrom = (disposition, examId) => {
    const match = /filename\*?=(?:UTF-8''|"?)([^";]+)/i.exec(disposition || '')
    if (!match?.[1]) return `certificate-${examId}.pdf`
    return decodeURIComponent(match[1].replace(/"/g, '').trim())
}

const showInTab = (fileUrl, preview) => {
    if (preview && !preview.closed) {
        preview.location.href = fileUrl
        return true
    }
    return Boolean(window.open(fileUrl, '_blank'))
}

export const openStudentExamCertificate = (payload, { view, examId, preview }) => {
    const data = payload?.data
    if (!(data instanceof Blob)) {
        const url = data?.url || data?.data?.url || data?.file_url || data?.data?.file_url
        if (url && view) return showInTab(url, preview)
        if (preview && !preview.closed) preview.close()
        return false
    }
    const contentType = payload?.headers?.['content-type'] || data.type || 'application/pdf'
    const file = data.type === contentType ? data : new Blob([data], { type: contentType })
    const fileUrl = URL.createObjectURL(file)
    if (view) {
        const opened = showInTab(fileUrl, preview)
        if (!opened) {
            URL.revokeObjectURL(fileUrl)
            return false
        }
        setTimeout(() => URL.revokeObjectURL(fileUrl), 60000)
        return true
    }
    if (preview && !preview.closed) preview.close()
    const link = document.createElement('a')
    link.href = fileUrl
    link.download = fileNameFrom(payload?.headers?.['content-disposition'], examId)
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(fileUrl), 1000)
    return true
}

export const downloadStudentExamRecording = createAsyncThunk('downloadStudentExamRecording', async ({ examId, navigate }) => {
    try {
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + `student_exam_recording/download?examId=${encodeURIComponent(examId)}`,
            responseType: 'blob',
            headers: { "Authorization": SessionManager.getCookie("user_token") }
        })
        return res
    } catch (error) {
        return error.response
    }
})

const downloadStudentExamRecordingSlice = createSlice({
    name: 'downloadStudentExamRecording',
    initialState: { data: null },
    extraReducers: (builder) => {
        builder.addCase(studentExamCertificate.fulfilled, (state, action) => {
            if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate);
            } else if (action?.payload?.status === 403) {
                showAccessDeniedAlert(action.meta.arg.navigate)
            }
        });
        builder.addCase(downloadStudentExamRecording.fulfilled, (state, action) => {
            if (action?.payload?.status === 200) {
                state.data = { examId: action.meta.arg.examId };
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate);
            } else if (action?.payload?.status === 403) {
                showAccessDeniedAlert(action.meta.arg.navigate)
            } else {
                state.data = null
            }
        });
    }
})

export default downloadStudentExamRecordingSlice.reducer;
